import { useMemo, useState } from 'react';
import { useAuthCtx } from '../lib/auth-context';
import { useOrderedCol } from '../lib/hooks';
import { EmptyState, Loading, toast } from '../components/ui';
import { CoursesTab } from '../components/admin/CoursesTab';
import { ChaptersTab } from '../components/admin/ChaptersTab';
import { ToolsTab } from '../components/admin/ToolsTab';
import { SettingsTab } from '../components/admin/SettingsTab';

const TABS = ['Courses', 'Chapters & sections', 'Tools', 'Site settings'] as const;
type Tab = (typeof TABS)[number];

const NAV: Array<{ tab: Tab; icon: string; label: string; blurb: string }> = [
  { tab: 'Courses', icon: '▦', label: 'Courses', blurb: 'Codes, colours & tags' },
  { tab: 'Chapters & sections', icon: '◈', label: 'Content tree', blurb: 'Chapters, sections, files' },
  { tab: 'Tools', icon: '⚙', label: 'Tools', blurb: 'Utilities on the site' },
  { tab: 'Site settings', icon: '✦', label: 'Settings', blurb: 'Name, tagline & XP' }
];

export default function AdminPage() {
  const auth = useAuthCtx();
  const [tab, setTab] = useState<Tab>('Courses');

  if (auth.loading) return <Loading label="Checking your access…" />;

  if (!auth.isSignedIn) {
    return (
      <EmptyState
        glyph="✦"
        title="Admin access"
        body="This dashboard manages what appears on the site. Everything you publish here is public, but the course content itself stays freely browsable without signing in."
        action={
          <button
            className="btn btn-primary"
            onClick={() => void auth.signInWithGoogle().catch(e => toast(String((e as Error)?.message ?? e), 'err'))}
          >
            Continue with Google
          </button>
        }
      />
    );
  }

  if (!auth.isAdmin) {
    return (
      <EmptyState
        glyph="⚠"
        title="This account is not an admin"
        body={`${auth.user?.email ?? 'You'} is not on the admin allow-list. Sign out and use the admin account to manage content.`}
        action={
          <button className="btn btn-outline" onClick={() => void auth.logout()}>
            Sign out
          </button>
        }
      />
    );
  }

  return <Dashboard tab={tab} setTab={setTab} email={auth.user?.email ?? ''} />;
}

function Dashboard({ tab, setTab, email }: { tab: Tab; setTab: (t: Tab) => void; email: string }) {
  const courses = useOrderedCol<{ hidden: boolean }>('courses');
  const chapters = useOrderedCol<{ hidden: boolean }>('chapters');
  const sections = useOrderedCol<{ hidden: boolean }>('sections');
  const resources = useOrderedCol<{ hidden: boolean }>('resources');
  const tools = useOrderedCol<{ hidden: boolean }>('tools');

  const ready = !courses.loading && !chapters.loading && !sections.loading && !resources.loading && !tools.loading;

  const counts = useMemo(() => {
    const arrs = [courses.data, chapters.data, sections.data, resources.data, tools.data];
    let total = 0;
    let hidden = 0;
    for (const arr of arrs) {
      for (const d of arr) {
        total++;
        if ((d as { hidden?: boolean }).hidden) hidden++;
      }
    }
    return { published: total - hidden, hidden };
  }, [courses.data, chapters.data, sections.data, resources.data, tools.data]);

  const navCount = (t: Tab): number | undefined => {
    if (!ready) return undefined;
    switch (t) {
      case 'Courses': return courses.data.length;
      case 'Chapters & sections': return chapters.data.length + sections.data.length + resources.data.length;
      case 'Tools': return tools.data.length;
      default: return undefined;
    }
  };

  const current = NAV.find(n => n.tab === tab)!;

  return (
    <div className="admin-shell">
      <aside className="admin-side">
        <div className="side-brand">
          <span className="tick" aria-hidden /> CONTROL ROOM
        </div>
        <nav className="admin-nav" role="tablist" aria-label="Admin sections">
          {NAV.map(n => {
            const nCount = navCount(n.tab);
            return (
              <button
                key={n.tab}
                className="side-item"
                role="tab"
                aria-selected={tab === n.tab}
                onClick={() => setTab(n.tab)}
              >
                <span className="ic" aria-hidden>{n.icon}</span>
                <span className="lbl">
                  {n.label}
                  <small>{n.blurb}</small>
                </span>
                {nCount !== undefined && <span className="n">{nCount}</span>}
              </button>
            );
          })}
        </nav>
        <div className="side-stats">
          <div className="stat">
            <b className="ok">{ready ? counts.published : '—'}</b>
            <label>published</label>
          </div>
          <div className="stat">
            <b className="warn">{ready ? counts.hidden : '—'}</b>
            <label>hidden</label>
          </div>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-hero">
          <div>
            <div className="eyebrow">
              <span className="tick" aria-hidden />
              ADMIN · CONTROL ROOM
            </div>
            <h1 className="page-title" style={{ margin: '8px 0 4px' }}>{current.label}</h1>
            <div className="who">
              <span className="live" aria-hidden />
              {current.blurb} · {email} {ready ? `· ${counts.published} live / ${counts.hidden} hidden` : '· syncing content…'}
            </div>
          </div>
          <div className="hero-acts">
            <a className="btn btn-outline btn-sm" href="/" >
              View site ↗
            </a>
          </div>
        </header>

        {tab === 'Courses' && <CoursesTab />}
        {tab === 'Chapters & sections' && <ChaptersTab />}
        {tab === 'Tools' && <ToolsTab />}
        {tab === 'Site settings' && <SettingsTab />}
      </main>
    </div>
  );
}
